/*
src/data/SDImageBank.js
wgbh-skydiagram-testing
astro.unl.edu
2019-07-14
*/


import {CallbackMakerMixin} from '../utils/CallbackMakerMixin.js';

import {SDImage} from './SDImage.js';


/*

Callbacks:
	change	- an image has been added or removed from the bank

*/


export class SDImageBank {

	static upgradeDatabase(db, bankIdentifier) {
		db.createObjectStore(bankIdentifier+'-images-metadata', {keyPath: 'id'});
		db.createObjectStore(bankIdentifier+'-images-binaries');
	}

	static getDefaultDef(bankIdentifier, basePath) {

		if (bankIdentifier === 'sun') {
			return {images: [
				{	id: 'none',
					name: 'none',
					src: '',
					type: '',
					isInternal: false,
					isLocked: true,
					aspectRatio: 1,
				},	
				{	id: 'current',
					name: 'current',
					src: basePath + '/graphics/sun.svg',
					type: '',
					isInternal: false,
					isLocked: true,
					aspectRatio: 1,
				},
			]};
		} else if (bankIdentifier === 'moon') {
			return {images: [
				{	id: 'none',
					name: 'none',
					src: '',
					type: '',
					isInternal: false,
					isLocked: true,
					aspectRatio: 1,
				},	
				{	id: 'current',
					name: 'current (v7 realistic cartoon)',
					src: basePath + '/graphics/Boston1_v7_moon-realisticcartoon.svg',
					type: '',
					isInternal: false,
					isLocked: true,
					aspectRatio: 1,
				},	
			]};
		} else if (bankIdentifier === 'ground') {
			return {images: [
				{	id: 'none',
					name: 'no image',
					src: '',
					type: '',
					isInternal: false,
					isLocked: true,
					aspectRatio: 1,
				},	
				{	id: 'current',
					name: 'current',
					src: basePath + '/graphics/ground.svg',
					type: '',
					isInternal: false,
					isLocked: true,
					aspectRatio: 1,
				},	
			]};
		} else if (bankIdentifier === 'foreground-objects') {
			return {images: [
				{ name: 'tree',
					id: 'tree',
					src: basePath + '/graphics/tree.svg',
					type: '',
					isInternal: false,
					isLocked: true,
					aspectRatio: 150/175,
				},
				{ name: 'house',
					id: 'house',
					src: basePath + '/graphics/house.svg',
					type: '',
					isInternal: false,
					isLocked: true,
					aspectRatio: 150/130,
				},
				{ name: 'lamp pane off',
					id: 'lamp-pane-off',	
					src: basePath + '/graphics/lamp-pane-off.svg',
					type: '',
					isInternal: false,
					isLocked: true,
					aspectRatio: 2,
				},
				{ name: 'lamp pane on',
					id: 'lamp-pane-on',	
					src: basePath + '/graphics/lamp-pane-on.svg',
					type: '',
					isInternal: false,
					isLocked: true,
					aspectRatio: 2,
				},
				{ name: 'lamp frame',
					id: 'lamp-frame',	
					src: basePath + '/graphics/lamp-frame.svg',
					type: '',
					isInternal: false,
					isLocked: true,
					aspectRatio: 2,
				},
			]};
		} else {
			return {images: []};
		}
	}


	toString() {
		return 'SDImageBank ('+this._bankIdentifier+')';
	}

	constructor(workspace, bankIdentifier, initObj) {

		// SDImage will access _metadataStoreName, _binariesStoreName, and _db directly.

		this._isReady = false;

		this._db = null;
		this._metadataStoreName = bankIdentifier + '-images-metadata';
		this._binariesStoreName = bankIdentifier + '-images-binaries';
		this._bankIdentifier = bankIdentifier;

		this._workspace = workspace;

		this._images = [];

		if (typeof initObj === 'object') {
			if (initObj.hasOwnProperty('def')) {
				this._initWithDef(initObj.def);
			} else if (initObj.hasOwnProperty('database')) {
				let useDefaults = Boolean(initObj.useDefaults);
				this._initWithDatabase(initObj.database, useDefaults);
			}
		}

		CallbackMakerMixin.apply(this);
	}
	
	_initWithDef(def) {
		for (let i = 0; i < def.images.length; ++i) {
			let image = new SDImage(this._workspace, this, {def: def.images[i]});
			this._images[i] = image;
		}
		this._isReady = true;
	}

	_initWithDatabase(db, useDefaults) {
		this._db = db;
		if (useDefaults) {
			this._initWithDef(SDImageBank.getDefaultDef(this._bankIdentifier, this._workspace.getBasePath()));
		} else {
			let t = this._db.transaction([this._metadataStoreName, this._binariesStoreName], 'readonly');
			let r = t.objectStore(this._metadataStoreName).openCursor();
			r.onsuccess = (e) => {
				let c = e.target.result;
				if (c) {
					let image = new SDImage(this._workspace, this, {transaction: t, metadata: c.value});
					this._images.push(image);
					c.continue();
				} else {
					this._makeReadyCallback();
				}
			};
			r.onerror = (e) => {
				console.error('Error loading images from database. Error: ' + e.target.errorCode);
			};
		}
	}

	getIsReady() {
		return this._isReady;
	}

	_makeReadyCallback() {
		this._isReady = true;
		this.makeCallback('ready');
	}

	getImages() {
		return this._images.slice();
	}

	getImageWithId(id) {
		// Returns an SDImage or null.
		if (id === null || id === '') {
			return null;
		}
		for (const image of this._images) {
			if (image._id === id) {
				return image;
			}
		}
		console.error('Failed to find image by id.');
		return null;
	}


	addNewImageFromFile(file) {
		// TODO: make async, load and read file before creating image object
		
		let image = new SDImage(this._workspace, this, {file: file});
		this._images.push(image);

		this.makeCallback('change');
	}


	deleteImage(image) {
		let index = this._images.indexOf(image);
		if (index >= 0) {
			if (image.getCanDelete()) {
				image.deleteFromDatabase();
				this._images.splice(index, 1);
				this.makeCallback('change');
			} else {
				console.error('Image can not be deleted.');
			}
		} else {
			console.error('Could not find image to delete.');
		}
	}

}


